import { useEffect, useState } from 'react';
import {
  getCategoryIDToCategoryMap,
  getChartData,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/chart';
import fr from 'facet-rest';
import { AllocationLoadingData } from 'Consts/BalanceAndAllocationLoadingData';

const GROUP_NAME_UNGROUPED = 'Ungrouped';

function useManagedAccountPortfolioWithGroupedHoldings(portfolio) {
  const [selectedCategory, setSelectedCategory] = useState();
  const [holdingsGroups, setHoldingsGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [usingMockData, setUsingMockData] = useState(false);

  const categoryIds = categories.map(cat => cat.id);
  const sortHoldingsByCategory = (holdings) => {
    return holdings.sort((a, b) => {
      const indexA = categoryIds.indexOf(a.attr('tickerClassification'));
      const indexB = categoryIds.indexOf(b.attr('tickerClassification'));
      if (indexA === -1) return 1; // Move unmatched to end
      if (indexB === -1) return -1; // Move unmatched to end
      return indexA - indexB;
    });
  };

  useEffect(() => {
    const cb = async () => {
      const holdingsGroupCollection = await portfolio.withRelationship(
        fr.relationships.holdingsGroup.name,
        async (rel) => rel.link('related').get(),
        () => undefined,
      );

      setSelectedCategory(null);
      if (holdingsGroupCollection && holdingsGroupCollection.resources().length() > 0) {
        setHoldingsGroups(holdingsGroupCollection.resources().list());
        setCategories(getChartData(portfolio, selectedCategory));
        setUsingMockData(false);
      } else {
        setCategories(getChartData(fr.resource(AllocationLoadingData[0]), selectedCategory));
        setUsingMockData(true);
      }
    };
    cb();
  }, [portfolio]);
  const filteredCategories = selectedCategory ? categories?.filter(d => d.label === selectedCategory) : categories;

  function filterHoldings(category, holdings) {
    return holdings?.filter((h) => category === null || h.attr('tickerClassification') === selectedCategory);
  }

  function filterHoldingsInHoldingsGroups(category, holdingsGroupsParam) {
    return holdingsGroupsParam.map((holdingsGroup) => ({
      name: holdingsGroup.attr('name'),
      total: holdingsGroup.attr('total'),
      holdings: sortHoldingsByCategory(filterHoldings(category, holdingsGroup.attr('holdings').resources().list())),
    })).filter((holdingsGroupObj) => holdingsGroupObj.holdings.length > 0);
  }

  const filteredHoldingsGroups = filterHoldingsInHoldingsGroups(selectedCategory, holdingsGroups);
  const filteredHoldingsGroupsWithoutUngrouped = filteredHoldingsGroups.filter((holdingsGroup) => holdingsGroup.name !== GROUP_NAME_UNGROUPED);
  const filteredUngroupedHoldings = filteredHoldingsGroups.find((holdingsGroup) => holdingsGroup.name === GROUP_NAME_UNGROUPED)?.holdings || [];

  return {
    selectedCategory,
    setSelectedCategory,
    filteredCategories,
    categories,
    filteredHoldingsGroups,
    holdingsGroups,
    filteredHoldingsGroupsWithoutUngrouped,
    filteredUngroupedHoldings,
    usingMockData,
    categoryIDToCategoryMap: getCategoryIDToCategoryMap(portfolio, selectedCategory),
  };
}

function useManagedAccountPortfolioLegacy(portfolio) {
  const [selectedCategory, setSelectedCategory] = useState();
  const [categories, setCategories] = useState([]);
  const [usingMockData, setUsingMockData] = useState(false);

  useEffect(() => {
    const account = portfolio?.attr('accounts').resources().head();
    const _holdings = account?.attr('holdings');
    setSelectedCategory(null);
    if (portfolio && _holdings.resources().length() > 0) {
      setCategories(getChartData(portfolio, selectedCategory));
      setUsingMockData(false);
    } else {
      setCategories(getChartData(fr.resource(AllocationLoadingData[0]), selectedCategory));
      setUsingMockData(true);
    }
  }, [portfolio]);

  const filteredCategories = selectedCategory ? categories?.filter(d => d.label === selectedCategory) : categories;
  const holdings = portfolio?.attr('accounts')?.resources().list()
    .map(acc => acc.attr('holdings')?.resources().list() || [])
    .flat() || [];

  function filterHoldings(category) {
    return holdings?.filter((h) => {
      const fields = h.meta().field('tickerClassification').options();
      const found = fields.findItem((i) => i.attr('id') === h.attr('tickerClassification'));
      return found?.attr('prompt') === category.label;
    });
  }

  const filteredHoldings = filteredCategories.map(category => filterHoldings(category).map(holding => ({ holding, category }))).flat();

  return {
    selectedCategory,
    setSelectedCategory,
    categories,
    filteredCategories,
    filteredHoldings,
    usingMockData,
  };
}

export function useManagedAccountPortfolio(portfolio) {
  const hookFn = portfolio.withRelationship(fr.relationships.holdingsGroup.name,
    () => useManagedAccountPortfolioWithGroupedHoldings,
    () => useManagedAccountPortfolioLegacy);

  return hookFn(portfolio);
}
